"use client";

import type { ComponentType } from "react";
import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { AnnouncementChip, ReviewBadges, TrustBenefitList } from "@/components/ui";
import { buttonVariants } from "@/components/ui/button";
import { cn, scrollToId } from "@/lib/utils";
import type { CTAConfig } from "@/components/sections/products/shared/hero-base/types";

/* Product hero in the homepage's idiom — the same gradient ribbon backdrop,
   announcement chip, glowing primary CTA and benefit list — but centred, and
   with no three.js weave: the homepage streams that chunk in behind its own
   hero, and a product page shouldn't pay for it.

   Shared rather than page-local: the residential hero can take the same shape
   by swapping which base its wrapper renders. */

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

type BenefitIcon = ComponentType<{
  size?: number;
  className?: string;
  "aria-hidden"?: "true";
}>;

interface Props {
  headingId: string;
  headingLine1: string;
  headingLine2: string;
  description: string;
  ctas: CTAConfig[];
  /** Overrides the default KYC / cancel-anytime / support row. */
  benefits?: { label: string; icon: BenefitIcon }[];
  announcement?: { href: string; label: string; badge?: string };
  onAnnouncementClick?: () => void;
}

export default function ProductHeroRibbon({
  headingId,
  headingLine1,
  headingLine2,
  description,
  ctas,
  benefits,
  announcement,
  onAnnouncementClick,
}: Props) {
  const prefersReducedMotion = useReducedMotion();

  const smoothAnchorClick = (href: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    // 136, not the default 96: anchor targets are usually a SectionHeader,
    // whose eyebrow label sits ~40px above the heading id we scroll to.
    scrollToId(href.slice(1), 136);
  };

  return (
    <section
      className="border-border relative flex flex-col justify-center overflow-y-clip border-b py-24 sm:min-h-[100svh] sm:py-28"
      aria-labelledby={headingId}
    >
      {/* Same gradient ribbon the homepage hero parks off its top-right corner,
          fading into the page on the left so the copy keeps its contrast.
          `band` pins it to a fixed strip from the top rather than to this
          section's height, so it lands exactly where the homepage's does. */}
      <div className="hero-ribbon-wrap band fade-left" aria-hidden="true">
        <div className="hero-ribbon" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="visible"
          animate="visible"
          className="mx-auto flex max-w-5xl flex-col items-center text-center"
        >
          {announcement && (
            <AnnouncementChip
              href={announcement.href}
              label={announcement.label}
              badge={announcement.badge}
              variant="white"
              onClick={onAnnouncementClick}
            />
          )}

          <h1
            id={headingId}
            className={cn(
              "text-foreground text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl",
              announcement && "mt-5"
            )}
          >
            {headingLine1}
            <br />
            {headingLine2}
          </h1>

          {/* muted-foreground is #5e5f6e on the light theme, which reads washed
              out under the h1 — darken it there only, so dark stays as tuned */}
          <p className="text-muted-foreground light:text-foreground/80 mt-6 max-w-2xl text-sm leading-relaxed font-medium sm:text-base">
            {description}
          </p>

          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-col items-center"
          >
            <div className="flex flex-wrap items-center justify-center gap-3">
              {ctas.map((cta, i) => {
                const isPrimary = i === 0;
                const isAnchor = cta.href.startsWith("#");
                const content = (
                  <>
                    {cta.leadingIcon}
                    {cta.label}
                    {cta.trailingIcon}
                  </>
                );

                /* The primary keeps the homepage's glow treatment. */
                if (isPrimary) {
                  return (
                    <span key={cta.href} className="cta-glow-btn-wrap">
                      {isAnchor ? (
                        <a
                          href={cta.href}
                          onClick={smoothAnchorClick(cta.href)}
                          className="cta-glow-btn-inner"
                        >
                          {content}
                        </a>
                      ) : (
                        <Link
                          href={cta.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cta-glow-btn-inner"
                        >
                          {content}
                        </Link>
                      )}
                    </span>
                  );
                }

                const className = cn(
                  buttonVariants({ variant: "outline", size: "lg" }),
                  "font-medium"
                );
                return isAnchor ? (
                  <a
                    key={cta.href}
                    href={cta.href}
                    onClick={smoothAnchorClick(cta.href)}
                    className={className}
                  >
                    {content}
                  </a>
                ) : (
                  <Link key={cta.href} href={cta.href} className={className}>
                    {content}
                  </Link>
                );
              })}
            </div>

            <TrustBenefitList
              items={benefits}
              className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2"
            />
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="border-border/50 mt-8 flex w-full max-w-xl flex-wrap items-center justify-center gap-3 border-t pt-8"
            aria-label="Review platforms"
          >
            <ReviewBadges />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
